// Same-day turnover helpers for the Step 1 date pickers.
//
// The availability endpoint (GET /api/trailers/:id/availability) returns, per
// booking, the greyed run [start, end] where end = last blocked day
// (blocked_until - 1 on the server), plus the booking's own checkIn/checkOut,
// and the trailer's turnaroundDays. Occupancy is half-open on the server:
// [check_in, blocked_until). So with a 0-day buffer a customer may CHECK OUT
// on the day another booking checks in: that day is greyed for check-in but
// must stay selectable for check-out. With a buffer of N the check-out must be
// N days before the next check-in. These helpers compute what the check-out
// picker should allow for a given check-in. External (Outdoorsy/RVshare)
// blocks are inclusive on both ends server-side, so check-out never lands on
// one of those.
import { parseLocalDate, formatLocalYmd } from '../utils/dates';

export const EMPTY_AVAILABILITY = Object.freeze({
  bookedDateStrings: new Set(),
  checkInDays: new Set(),
  turnaroundDays: 0,
});

export function addDaysYmd(ymd, days) {
  const d = parseLocalDate(ymd);
  if (!d) return ymd;
  d.setDate(d.getDate() + days);
  return formatLocalYmd(d);
}

// Expand the API response into the sets the pickers need. Each range is
// expanded to individual calendar days via parseLocalDate + formatLocalYmd so
// we never round-trip through .toISOString() (the TZ-shift class of bugs).
export function buildAvailability(data) {
  const bookedDateStrings = new Set();
  const checkInDays = new Set();
  (data?.bookedRanges || []).forEach((range) => {
    const start = parseLocalDate(range.start);
    const end = parseLocalDate(range.end);
    if (!start || !end) return;
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      bookedDateStrings.add(formatLocalYmd(d));
    }
    if (range.status !== 'external') {
      const ci = parseLocalDate(range.checkIn || range.start);
      if (ci) checkInDays.add(formatLocalYmd(ci));
    }
  });
  const t = Number(data?.turnaroundDays);
  return {
    bookedDateStrings,
    checkInDays,
    turnaroundDays: Number.isInteger(t) && t >= 0 ? t : 0,
  };
}

// What the check-out picker may offer once a check-in is chosen:
//   - bookedDateStrings: days to grey out (the next booking's check-in day is
//     un-greyed when the buffer is 0, since checking out that day is allowed)
//   - maxDate: latest selectable check-out (null = no cap). The range may not
//     span a booked day, so the cap is the first booked day after check-in,
//     minus the buffer (minus 1 for an external block).
export function checkOutConstraints({ bookedDateStrings, checkInDays, turnaroundDays = 0, checkIn }) {
  const booked = bookedDateStrings || new Set();
  if (!checkIn || booked.size === 0) return { bookedDateStrings: booked, maxDate: null };

  let first = null;
  booked.forEach((d) => {
    if (d > checkIn && (first === null || d < first)) first = d;
  });
  if (first === null) return { bookedDateStrings: booked, maxDate: null };

  if (checkInDays && checkInDays.has(first)) {
    const t = Math.max(0, Math.trunc(Number(turnaroundDays)) || 0);
    if (t === 0) {
      const relaxed = new Set(booked);
      relaxed.delete(first);
      return { bookedDateStrings: relaxed, maxDate: first };
    }
    return { bookedDateStrings: booked, maxDate: addDaysYmd(first, -t) };
  }
  return { bookedDateStrings: booked, maxDate: addDaysYmd(first, -1) };
}
